import { registerMockAPI } from '~/mocks/mockApis';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { makeListResponse } from '~/mocks/utils/listFixtures';
import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import { receiveKeys } from './keys';

import type { TypeReceiveReportHistory } from '~/types/receive.types';
import type {
  TypeGetReceivingReportHistoriesParams,
  TypeGetReceivingReportHistoriesResponse,
} from '~/types/receive.types';

const fetchReportHistories = async (
  ownerCode: string,
  params: TypeGetReceivingReportHistoriesParams
): Promise<TypeGetReceivingReportHistoriesResponse> => {
  const { data } = await axiosInstance.get<TypeGetReceivingReportHistoriesResponse>(
    `${API_ENDPOINT.RECEIVE.GET_REPORT_HISTORIES}/${ownerCode}/report/histories`,
    { params }
  );
  return data;
};

type TypeUseGetReportHistoriesQueryParams = {
  ownerCode: string;
  params: TypeGetReceivingReportHistoriesParams;
  enabled?: boolean;
};

/**
 * [입고관리 > 입고 리포트 관리] 입고 리포트 목록 조회 Query Hook
 */
export const useGetReportHistoriesQuery = ({
  ownerCode,
  params,
  enabled = true,
}: TypeUseGetReportHistoriesQueryParams) => {
  return useQuery({
    queryKey: receiveKeys.reportHistories(ownerCode, params),
    queryFn: () => fetchReportHistories(ownerCode, params),
    enabled,
    staleTime: 0,
    gcTime: 1000 * 30,
    placeholderData: keepPreviousData,
  });
};

// 5건 리포트 상태 4종(REGISTERED/COMPLETED/CANCELED/NONE) + receivedDateTime optional + taskNames 1~2건 분기를 모두 포함
// idx 1: REGISTERED (발행, 입고 확정 전)
// idx 2: COMPLETED (처리완료, 입고 확정됨)
// idx 3: CANCELED (발행취소, 입고 확정됨)
// idx 4: NONE (미발행, 입고 확정 전)
// idx 5: COMPLETED (작업유형 2개)
const REPORT_FIXTURES: Array<Omit<TypeReceiveReportHistory, 'id' | 'reportCode' | 'requestCode'>> = [
  {
    reportStatus: 'REGISTERED',
    releaseName: '컬리배차(직배송)',
    sku: 5,
    unit: 120,
    taskNames: ['벌크해체'],
    receivingExpectedDate: '2026-05-14',
    receivingExpectedTime: '10:00~12:00',
    receivedDateTime: undefined,
    createdAt: '2026-05-13T15:30:00',
    statusUpdatedAt: '2026-05-13T15:30:00',
  },
  {
    reportStatus: 'COMPLETED',
    releaseName: '컬리배차(직배송)',
    sku: 3,
    unit: 60,
    taskNames: ['벌크해체'],
    receivingExpectedDate: '2026-05-12',
    receivingExpectedTime: '14:00~16:00',
    receivedDateTime: '2026-05-12T16:30:00',
    createdAt: '2026-05-12T14:00:00',
    statusUpdatedAt: '2026-05-12T17:00:00',
  },
  {
    reportStatus: 'CANCELED',
    releaseName: '밀크런(픽업)',
    sku: 4,
    unit: 80,
    taskNames: ['스티커부착'],
    receivingExpectedDate: '2026-05-11',
    receivingExpectedTime: '09:00~11:00',
    receivedDateTime: '2026-05-11T18:00:00',
    createdAt: '2026-05-11T10:00:00',
    statusUpdatedAt: '2026-05-11T12:00:00',
  },
  {
    reportStatus: 'NONE',
    releaseName: '컬리배차(픽업)',
    sku: 2,
    unit: 40,
    taskNames: [],
    receivingExpectedDate: '2026-05-15',
    receivingExpectedTime: '13:00~15:00',
    receivedDateTime: undefined,
    createdAt: '2026-05-13T11:00:00',
    statusUpdatedAt: '2026-05-13T11:00:00',
  },
  {
    reportStatus: 'COMPLETED',
    releaseName: '컬리배차(직배송)',
    sku: 1,
    unit: 20,
    taskNames: ['벌크해체', '스티커부착'],
    receivingExpectedDate: '2026-05-10',
    receivingExpectedTime: '11:00~13:00',
    receivedDateTime: '2026-05-10T15:00:00',
    createdAt: '2026-05-10T09:00:00',
    statusUpdatedAt: '2026-05-10T15:30:00',
  },
];

const makeReportItem = (index: number): TypeReceiveReportHistory => {
  const fixture = REPORT_FIXTURES[(index - 1) % REPORT_FIXTURES.length];
  return {
    id: 8000 + index,
    reportCode: `RP20260513_${String(index).padStart(5, '0')}`,
    requestCode: `OR20260513_${String(index).padStart(5, '0')}`,
    ...fixture,
  };
};

const buildReportListResponse = (count: number): TypeGetReceivingReportHistoriesResponse =>
  makeListResponse(count, makeReportItem, 'reportHistories', {
    listView: 50,
    listTotal: count,
    cursor: 0,
  });

registerMockAPI<TypeGetReceivingReportHistoriesResponse>({
  method: 'get',
  endpoint: '/api-inbound/v1/3pl/receiving/:ownerCode/report/histories',
  label: '입고/입고리포트/목록',
  scenarios: [
    { name: '정상 응답 — 5건 (발행/완료/취소/미발행 혼합)', response: buildReportListResponse(5) },
    { name: '정상 응답 — 0건 (빈 목록)', response: buildReportListResponse(0) },
    {
      name: '잘못된 파라미터 (9001)',
      response: { error: { code: 9001, message: 'Error Occured By Not Valid Parameters' } },
      status: 400,
    },
  ],
});
